import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { CopyButton } from "../../../components/buttons/copyButton";
import {
  EventCard,
  EventCardSkeleton,
} from "../../../components/event/eventCard";
import { TabNav } from "../../../components/tabNav";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useGetEventGuestsQuery,
  useGetEventQuery,
  useGetEventTicketsBreakdownQuery,
  useGetEventTicketsQuery,
} from "../../../redux/features/event/event.api";
import {
  formatDate,
  formatTime,
  getEventUrl,
  getRandomColor,
  handleShare,
  parseTimeToDate,
} from "../../../utils/helpers";

import Dropdown from "../../../components/dropdown/dropdown";
import { Icon } from "../../../components/icon/icon";
import { EventMoreOptionsModal } from "../../../components/modal/app_modals/eventMoreOptionsModal";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { TICKET_COLOR_OPTIONS } from "../../../utils/constants";
import { TicketSalesBreakdown } from "../../../utils/types";
import { EventGuests } from "./components/eventGuests";
import { EventOverview } from "./components/eventOverview";
import { EventSettings } from "./components/eventSettings";
import { EventTickets } from "./components/eventTickets";

export type Tabs = "overview" | "guests" | "tickets" | "settings";

export const ViewSingleEvent = () => {
  const [activeTab, setActiveTab] = useState<Tabs>("overview");
  const [ticketBreakdown, setTicketBreakdown] = useState<TicketSalesBreakdown>(
    [],
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const { slug } = useParams<{ slug: string }>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: eventResponse,
    isLoading,
    refetch: refetchEventDetails,
  } = useGetEventQuery(
    {
      slug: slug,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: ticketPurchaseBreakdown } = useGetEventTicketsBreakdownQuery(
    {
      id: eventResponse?.data?._id!,
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  const { data: guests, isFetching: fetchingGuests } = useGetEventGuestsQuery(
    {
      id: eventResponse?.data?._id!,
      search: "",
      page: 1,
      limit: 10,
      sortOrder: "desc",
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  const {
    data: ticketsData,
    isFetching: fetchingTickets,
    refetch: refetchTickets,
  } = useGetEventTicketsQuery(
    {
      id: eventResponse?.data?._id!,
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  const navigate = useNavigate();

  const tabList = [
    {
      title: "Overview",
      to: "overview",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Guests",
      to: "guests",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Tickets",
      to: "tickets",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Settings",
      to: "settings",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
  ];

  const moreOptions = [
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="camera" width={20} height={20} className="text-grey-400" />{" "}
          Check-In Guests
        </p>
      ),
      onClick: () => {
        navigate(`/event/${eventResponse?.data._id}/guests`);
      },
    },
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="share" width={20} height={20} className="text-grey-400" />{" "}
          Go To Event
        </p>
      ),
      onClick: () => {
        navigate(`/events/${eventResponse?.data?.slug!}`);
      },
    },
    {
      name: (
        <p className="flex items-center gap-2">
          <Icon svg="upload" width={20} height={20} className="text-grey-400" />{" "}
          Share Event
        </p>
      ),
      onClick: async () => {
        await handleShare("", "", getEventUrl(eventResponse?.data?.slug ?? ""));
      },
    },
  ];

  // useEffect(() => {
  //   if (
  //     eventResponse?.data &&
  //     eventResponse?.data?.createdBy?.userType !== "organiser"
  //   ) {
  //     navigate("/events");
  //   }
  //   // eslint-disable-next-line
  // }, [eventResponse]);

  useEffect(() => {
    const selectedColors: string[] = [];

    if (ticketPurchaseBreakdown?.data) {
      setTicketBreakdown(
        ticketPurchaseBreakdown.data.map((t) => {
          const color = getRandomColor(
            TICKET_COLOR_OPTIONS,
            selectedColors,
            "sequential",
          );
          selectedColors.push(color);
          return {
            ticketId: t.ticketId,
            purchaseQuantity: t.totalQuantityPurchased,
            color: color,
          };
        }),
      );
    }
  }, [ticketPurchaseBreakdown]);

  return isLoading ? (
    <EventCardSkeleton imageDimension={400} showExtraDetails className="" />
  ) : (
    <div className="flex w-full animate-fade-in flex-col gap-2">
      <BackButton />

      <EventCard
        image={eventResponse?.data?.image!}
        name={eventResponse?.data?.name!}
        location={eventResponse?.data?.location!}
        startDate={formatDate(eventResponse?.data?.startDate!)}
        endDate={formatDate(eventResponse?.data?.endDate!)}
        startTime={formatTime(parseTimeToDate(eventResponse?.data?.startTime!))}
        endTime={formatTime(parseTimeToDate(eventResponse?.data?.endTime!))}
        noTickets={undefined}
        totalGuests={guests?.data?.totalCount ?? 0}
        isOrganizer
        event={eventResponse?.data!}
        className=""
        imageDimension={400}
      >
        <div className="grid flex-1 grid-cols-1 flex-col justify-between gap-4">
          <div className="flex flex-row items-center gap-[10px]">
            <Button
              type="button"
              onClick={() => {
                navigate(`/event/${eventResponse?.data?.slug}/edit`);
              }}
              text="Edit Event"
              icon="pencil"
              variation="plain-grey"
              className=""
              disabled={eventResponse?.data?.status === "completed"}
            />

            <Button
              type="button"
              onClick={() => {
                navigate(`/event/${eventResponse?.data._id}/guests`);
              }}
              text="Check In Guests"
              icon="calendar-check"
              variation="plain-grey"
              className="hidden md:flex"
              disabled={eventResponse?.data?.status === "completed"}
            />

            <Dropdown
              menuItems={moreOptions.slice(1)}
              menuClassName="!min-w-[220px] !border-grey-100 [--anchor-gap:8px]"
              position="bottom end"
              className="hidden !h-[50px] !min-w-[50px] md:flex"
              closeOnClick={false}
              menuItemsClassName="!text-base !font-bold"
            >
              <button
                type="button"
                className="flex h-full w-full items-center justify-center rounded-full border border-grey-950/[12%]"
              >
                <Icon
                  svg="ellipsis"
                  width={20}
                  height={20}
                  className="text-black"
                />
              </button>
            </Dropdown>

            <button
              type="button"
              className="flex !h-[50px] !min-w-[50px] items-center justify-center rounded-full border border-grey-950/[12%] md:hidden"
              onClick={() => {
                handleModalOpen("moreOptionsModal");
              }}
            >
              <Icon
                svg="ellipsis"
                width={20}
                height={20}
                className="text-black"
              />
            </button>
          </div>

          <div className="border-t pt-4">
            <p className="font-bold text-primary">Share your event!</p>
            <p className="text-sm text-grey-500">
              Promote this events on socials for more reach.
            </p>

            <div className="mt-3 flex w-full items-center justify-between gap-4 rounded-[100px] bg-grey-50 p-4 text-sm font-bold text-grey-950">
              <p className="line-clamp-1 max-w-full flex-1 overflow-hidden text-ellipsis">
                {getEventUrl(eventResponse?.data?.slug!)}
              </p>

              <CopyButton value={getEventUrl(eventResponse?.data?.slug!)} />
            </div>
          </div>
        </div>
      </EventCard>

      {/* <AddBankDetailsCTA /> */}

      <div className="flex flex-col gap-[2px]" ref={contentRef}>
        <BgWrapper className="!rounded-b-none">
          <TabNav
            navs={tabList}
            activeTab={activeTab}
            variant="switcher"
            className=""
            tabClassName=""
          />
        </BgWrapper>

        {activeTab === "overview" && eventResponse?.data !== undefined ? (
          <EventOverview
            event={eventResponse?.data!}
            setTab={setActiveTab}
            ticketBreakdown={ticketBreakdown}
            ticketsData={ticketsData}
            guests={guests}
            fetchingGuests={fetchingGuests}
          />
        ) : undefined}

        {activeTab === "guests" && eventResponse?.data !== undefined ? (
          <EventGuests
            event={eventResponse?.data!}
            setTab={setActiveTab}
            ticketBreakdown={ticketBreakdown}
          />
        ) : undefined}

        {activeTab === "tickets" && eventResponse?.data !== undefined ? (
          <EventTickets
            event={eventResponse?.data!}
            setTab={setActiveTab}
            data={ticketsData}
            fetchingTickets={fetchingTickets}
            refetchTickets={() => {
              refetchTickets();
            }}
          />
        ) : undefined}

        {activeTab === "settings" && eventResponse?.data !== undefined ? (
          <EventSettings
            event={eventResponse?.data!}
            setTab={setActiveTab}
            refetchEvent={() => {
              refetchEventDetails();
            }}
          />
        ) : undefined}
      </div>

      <EventMoreOptionsModal
        open={modal === "moreOptionsModal" && modalOpen}
        handleClose={() => {
          handleModalClose();
        }}
        options={moreOptions}
      />
    </div>
  );
};
